// Batched gallery assets. The old per-figure mesh class is gone — every image is
// now one instance of a single InstancedBufferGeometry (1 draw call), sampled
// from a DataArrayTexture (sampler2DArray). This module owns:
//   - the GLSL3 shaders (instanced image batch)
//   - buildImageArray(): packs all unique images into one array texture
//   - uvCrop(): object-fit cover/contain crop math (per figure, at resize)
// Gallery (./index.js) wires these into one mesh + per-frame uniforms.
//
// Motion here is 1D (vertical): the gallery's accumulated scroll (currentHeight)
// drives uScroll, and the column tiles by uPeriod (maxHeight) via a GPU `mod`
// wrap, so the per-frame update is a single uniform write. The chromatic
// lens look is unchanged — it stays in the post-process pass (Experience).
import * as THREE from 'three';

// Instanced image batch. RawShaderMaterial + GLSL3 so we can use sampler2DArray.
// Declare the built-ins (projection/modelView/position/uv) and precision
// ourselves; three still binds their values.
const IMAGE_VERT = /* glsl */ `
	precision highp float;
	uniform mat4 projectionMatrix;
	uniform mat4 modelViewMatrix;
	uniform float uScroll;   // gallery accumulated scroll (currentHeight, px)
	uniform float uPeriod;   // tiling period in px (maxHeight) — column repeat
	in vec3 position;        // plane vertex, xy in [-0.5, 0.5]
	in vec2 uv;
	in vec2 aBase;           // figure screen-center at scroll=0 (world px, origin = screen ctr)
	in vec2 aSize;           // plane px size (cover/contain baked)
	in vec2 aUvRepeat;
	in vec2 aUvOffset;
	in float aLayer;
	in vec2 aOffset;         // intro tf: position offset (px), 0 at rest
	in float aRot;           // intro tf: rotation (rad), 0 at rest
	in float aScale;         // intro tf: scale multiplier, 1 at rest
	in float aAlpha;         // intro tf: opacity, 1 at rest
	out vec2 vUv;
	out float vLayer;
	out float vAlpha;
	void main() {
		vUv = uv * aUvRepeat + aUvOffset;
		vLayer = aLayer;
		vAlpha = aAlpha;
		// authored transform (intro only) — scale + rotate the quad in screen space
		vec2 local = position.xy * aSize * aScale;
		float s = sin(aRot), co = cos(aRot);
		local = vec2(local.x * co - local.y * s, local.x * s + local.y * co);
		// +scroll moves the column up in world space (matches old extraY += speed)
		float y = (aBase.y + aOffset.y) + uScroll;
		y = mod(y + uPeriod * 0.5, uPeriod) - uPeriod * 0.5; // infinite wrap into one period
		vec2 c = vec2(aBase.x + aOffset.x, y);
		gl_Position = projectionMatrix * modelViewMatrix * vec4(c + local, -1.0, 1.0);
	}
`;

const IMAGE_FRAG = /* glsl */ `
	precision highp float;
	precision highp sampler2DArray;
	uniform sampler2DArray uArray;
	in vec2 vUv;
	in float vLayer;
	in float vAlpha;
	out vec4 outColor;
	void main() {
		outColor = texture(uArray, vec3(vUv, vLayer));
		outColor.a *= vAlpha; // intro fade-in
	}
`;

export const SHADERS = { IMAGE_VERT, IMAGE_FRAG };

// object-fit crop. Given the plane box (w,h) and image aspect, returns the
// plane's drawn px size + the uv crop rect (sample = uv * repeat + offset) into
// the *image*. daria's figures are all "cover".
export function uvCrop(mode, w, h, aspect) {
	const ab = w / h;
	const ai = aspect;
	if (mode === 'contain') {
		let sw = w;
		let sh = h;
		if (ai > ab) sh = w / ai;
		else sw = h * ai;
		return { width: sw, height: sh, repeat: [1, 1], offset: [0, 0] };
	}
	if (ai > ab) {
		const rx = ab / ai;
		return { width: w, height: h, repeat: [rx, 1], offset: [(1 - rx) / 2, 0] };
	}
	const ry = ai / ab;
	return { width: w, height: h, repeat: [1, ry], offset: [0, (1 - ry) / 2] };
}

// Aspect-preserving fit of an image into one LxL layer: the sub-rect (uv) the
// image occupies, centered, the rest left transparent. The cover/contain crop is
// later composed onto this so the letterbox is never sampled.
function fitRect(aspect) {
	if (aspect >= 1) {
		const fh = 1 / aspect;
		return { repeat: [1, fh], offset: [0, (1 - fh) / 2] };
	}
	const fw = aspect;
	return { repeat: [fw, 1], offset: [(1 - fw) / 2, 0] };
}

// Load + dedupe all image srcs, pack each into one layer of a DataArrayTexture
// (aspect-preserved, v-flipped so data row 0 = uv v 0). Returns the texture plus
// per-src { layer, fit, aspect }.
export function buildImageArray(srcs, L = 768) {
	const unique = Array.from(new Set(srcs));
	const canvas = document.createElement('canvas');
	canvas.width = L;
	canvas.height = L;
	const ctx = canvas.getContext('2d');

	const data = new Uint8Array(L * L * 4 * unique.length);
	const info = new Map();

	const loadOne = (src, layer) =>
		new Promise((resolve) => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => {
				const aspect = img.naturalWidth / img.naturalHeight || 1;
				const fit = fitRect(aspect);
				ctx.clearRect(0, 0, L, L);
				ctx.save();
				ctx.translate(0, L); // flip vertically: GL expects row 0 at the bottom
				ctx.scale(1, -1);
				ctx.drawImage(
					img,
					fit.offset[0] * L,
					fit.offset[1] * L,
					fit.repeat[0] * L,
					fit.repeat[1] * L
				);
				ctx.restore();
				data.set(ctx.getImageData(0, 0, L, L).data, layer * L * L * 4);
				info.set(src, { layer, fit, aspect });
				resolve();
			};
			img.onerror = () => {
				info.set(src, { layer, fit: fitRect(1), aspect: 1 });
				resolve();
			};
			img.src = src;
		});

	return Promise.all(unique.map((src, i) => loadOne(src, i))).then(() => {
		const tex = new THREE.DataArrayTexture(data, L, L, unique.length);
		tex.format = THREE.RGBAFormat;
		tex.type = THREE.UnsignedByteType;
		tex.colorSpace = THREE.LinearSRGBColorSpace;
		tex.magFilter = THREE.LinearFilter;
		tex.minFilter = THREE.LinearMipmapLinearFilter;
		tex.generateMipmaps = true;
		tex.needsUpdate = true;
		return { texture: tex, info };
	});
}
